// ================================================================
// PlatformPanel — overlay «همه شرکت‌ها» + سطل آشغال (فقط platform)
// ================================================================
import './platform.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml, delegate } from '../../utils/dom.js';
import {
  listOrgs, createOrg, getOrg, setOrgStatus, getOrgAudit,
  deleteOrg, listTrash, restoreOrg, purgeOrg, setOrgModules,
  setOrgSubscriptionExempt, setOrgTenderAuditAi,
  setOrgMaxSessions,
  inviteOrgMember,
} from '../../core/platform.js';
import { MODULE_CATALOG } from '../../core/moduleCatalog.js';

import { date as fmtDate, dateTime as fmtDateTime, percent as fmtPercent } from '../../utils/format.ts';

let overlay = null;
const s = {
  view: 'list', // 'list' | 'detail' | 'trash'
  orgs: [],
  trash: [],
  loading: false,
  error: '',
  creating: false,
  creatingBusy: false,
  createError: '',
  inviteUrl: '',
  inviteMailSent: false,  // آیا لینک با ایمیل برای مالک ارسال شد؟
  inviteEmail: '',
  detail: null,
  audit: [],
  confirmDelete: false,  // تأییدِ انتقال به سطل آشغال (در جزئیات)
  purgeConfirmId: '',     // تأییدِ حذف دائمی (در سطل آشغال)
  inviteMemberUrl: '',    // لینک دعوتِ تازه‌ساخته‌شده (در جزئیات شرکت)
  inviteMemberMailSent: false,
  inviteMemberEmail: '',
  inviteMemberBusy: false,
  inviteMemberError: '',
};

export function openPlatformPanel() {
  if (overlay) return;
  overlay = document.createElement('div');
  overlay.className = 'pf-overlay';
  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';
  delegate(overlay, '[data-action]', 'click', onAction);
  overlay.addEventListener('submit', onSubmit);
  overlay.addEventListener('input', onInput);
  resetTransient();
  s.view = 'list';
  loadList();
}

function resetTransient() {
  s.inviteUrl = ''; s.creating = false; s.error = '';
  s.confirmDelete = false; s.purgeConfirmId = '';
}

function close() {
  if (!overlay) return;
  overlay.remove();
  overlay = null;
  document.body.style.overflow = '';
}

async function loadList() {
  s.loading = true; s.error = ''; draw();
  try { s.orgs = (await listOrgs()).organizations || []; }
  catch (e) { s.error = e.message; }
  finally { s.loading = false; draw(); }
}

async function loadTrash() {
  s.loading = true; s.error = ''; s.purgeConfirmId = ''; draw();
  try { s.trash = (await listTrash()).organizations || []; }
  catch (e) { s.error = e.message; }
  finally { s.loading = false; draw(); }
}

async function openDetail(id) {
  s.view = 'detail'; s.loading = true; s.detail = null; s.audit = []; s.error = ''; s.confirmDelete = false;
  s.inviteMemberUrl = ''; s.inviteMemberBusy = false; s.inviteMemberError = ''; draw();
  try {
    const [d, a] = await Promise.all([getOrg(id), getOrgAudit(id).catch(() => ({ logs: [] }))]);
    s.detail = d.organization;
    s.audit = a.logs || [];
  } catch (e) { s.error = e.message; }
  finally { s.loading = false; draw(); }
}

// ── رویدادها ──
async function onAction(e, el) {
  const a = el.dataset.action;
  switch (a) {
    case 'close': return close();
    case 'new': s.creating = true; s.createError = ''; return draw();
    case 'cancel-new': s.creating = false; return draw();
    case 'dismiss-invite': s.inviteUrl = ''; return draw();
    case 'dismiss-member-invite': s.inviteMemberUrl = ''; return draw();
    case 'open': return openDetail(el.dataset.id);
    case 'back': s.view = 'list'; return loadList();
    case 'trash': s.view = 'trash'; return loadTrash();

    case 'copy': {
      try { await navigator.clipboard.writeText(el.dataset.url); } catch { /* blocked */ }
      const old = el.textContent;
      el.textContent = t('platform.created.copied');
      setTimeout(() => { if (el.isConnected) el.textContent = old; }, 1500);
      return;
    }

    case 'toggle-status': {
      const next = el.dataset.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
      el.disabled = true;
      try { await setOrgStatus(el.dataset.id, next); await openDetail(el.dataset.id); }
      catch (err) { s.error = err.message; draw(); }
      return;
    }

    case 'toggle-module': return el.classList.toggle('active');

    // Dauerzugang — wie oben: einzelner Schalter, sofort speichern
    case 'toggle-exempt': {
      const next = el.dataset.enabled !== '1';
      el.disabled = true;
      try { await setOrgSubscriptionExempt(el.dataset.id, next); await openDetail(el.dataset.id); }
      catch (err) { s.error = err.message; el.disabled = false; draw(); }
      return;
    }

    case 'toggle-tender-audit-ai': {
      const next = el.dataset.enabled !== '1';
      el.disabled = true;
      try { await setOrgTenderAuditAi(el.dataset.id, next); await openDetail(el.dataset.id); }
      catch (err) { s.error = err.message; el.disabled = false; draw(); }
      return;
    }

    // Geräte-Limit — anders als die Schalter darüber ein ZAHLENFELD, darum
    // wird hier gelesen statt umgeschaltet. Gesenkt wird niemand rückwirkend
    // abgemeldet: das Limit greift beim nächsten Login (siehe Backend-Route).
    case 'save-maxsessions': {
      const input = document.getElementById('pf-maxsessions');
      const value = Number(input?.value);
      if (!Number.isInteger(value) || value < 1 || value > 20) {
        s.error = t('platform.maxSessions.invalid');
        draw();
        return;
      }
      el.disabled = true;
      try { await setOrgMaxSessions(el.dataset.id, value); await openDetail(el.dataset.id); }
      catch (err) { s.error = err.message; el.disabled = false; draw(); }
      return;
    }

    case 'save-modules': {
      const mods = activeModules('detailModules');
      el.disabled = true;
      try { await setOrgModules(el.dataset.id, mods); await openDetail(el.dataset.id); }
      catch (err) { s.error = err.message; el.disabled = false; draw(); }
      return;
    }

    // حذف (انتقال به سطل آشغال) — از داخل جزئیات
    case 'ask-delete': s.confirmDelete = true; return draw();
    case 'cancel-delete': s.confirmDelete = false; return draw();
    case 'confirm-delete': {
      el.disabled = true;
      try { await deleteOrg(el.dataset.id); s.view = 'list'; await loadList(); }
      catch (err) { s.error = err.message; draw(); }
      return;
    }

    // سطل آشغال
    case 'restore': {
      el.disabled = true;
      try { await restoreOrg(el.dataset.id); await loadTrash(); }
      catch (err) { s.error = err.message; draw(); }
      return;
    }
    case 'ask-purge': s.purgeConfirmId = el.dataset.id; return draw();
    case 'cancel-purge': s.purgeConfirmId = ''; return draw();
    case 'confirm-purge': {
      el.disabled = true;
      try { await purgeOrg(el.dataset.id); await loadTrash(); }
      catch (err) { s.error = err.message; draw(); }
      return;
    }
  }
}

function onInput(e) {
  const el = e.target;
  if (el.name === 'slug') { el.dataset.touched = '1'; return; }
  if (el.name === 'name') {
    const slug = overlay.querySelector('input[name="slug"]');
    if (slug && !slug.dataset.touched) slug.value = slugify(el.value);
  }
}

// Muss dieselbe Form erzeugen wie slugify() im Backend (auth.service.ts) —
// sonst baut das Formular einen Slug, den der Server danach ablehnt.
// NFKD macht aus «Müller» → «muller» statt «m-ller».
function slugify(v) {
  return String(v)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

async function onSubmit(e) {
  if (e.target.id === 'pf-invite-form') return onInviteSubmit(e);
  if (e.target.id !== 'pf-new-form') return;
  e.preventDefault();
  const f = e.target.elements;
  const data = {
    name: f.namedItem('name').value.trim(),
    slug: f.namedItem('slug').value.trim(),
    ownerEmail: f.namedItem('ownerEmail').value.trim(),
  };
  const on = f.namedItem('ownerName').value.trim();
  if (on) data.ownerName = on;
  const mods = activeModules('modules');
  if (mods.length) data.enabledModules = mods;

  s.creatingBusy = true; s.createError = ''; draw();
  try {
    const r = await createOrg(data);
    s.inviteUrl = r.ownerInviteUrl;
    s.inviteMailSent = !!r.mailSent;
    s.inviteEmail = data.ownerEmail;
    s.creating = false; s.creatingBusy = false;
    await loadList();
  } catch (err) {
    s.creatingBusy = false;
    s.createError = err.status === 409 ? t('platform.errors.slugTaken') : (err.message || t('platform.errors.generic'));
    draw();
  }
}

// دعوت عضو برای شرکتِ باز — لینک برمی‌گردد و بالای فرم نمایش داده می‌شود
async function onInviteSubmit(e) {
  e.preventDefault();
  const f = e.target.elements;
  const orgId = e.target.dataset.id;
  const email = f.namedItem('inviteEmail').value.trim();
  const roleId = f.namedItem('inviteRole').value || null;

  s.inviteMemberBusy = true; s.inviteMemberError = ''; draw();
  try {
    const r = await inviteOrgMember(orgId, { email, roleId });
    s.inviteMemberMailSent = !!r.mailSent;
    s.inviteMemberEmail = email;
    // Detail neu laden (zeigt die neue Pending-Einladung), dann Link einblenden
    await openDetail(orgId);
    s.inviteMemberUrl = r.inviteUrl;
    draw();
  } catch (err) {
    s.inviteMemberBusy = false;
    s.inviteMemberError = err.status === 409 ? t('platform.invite.alreadyMember') : (err.message || t('platform.errors.generic'));
    draw();
  }
}

// ── رندر ──
function draw() {
  if (!overlay) return;
  let body;
  if (s.view === 'detail') body = detailHtml();
  else if (s.view === 'trash') body = trashHtml();
  else body = listHtml();

  overlay.innerHTML = `
    <div class="pf-backdrop" data-action="close"></div>
    <div class="pf-panel" role="dialog" aria-modal="true">
      <div class="pf-head">
        <div class="pf-head-title">${icon('corporate_fare', 22)}<span>${escapeHtml(t('platform.title'))}</span></div>
        <button class="pf-icon-btn" data-action="close">${icon('close', 20)}</button>
      </div>
      <div class="pf-body">${body}</div>
    </div>`;
}

function errBox(msg) {
  return `<div class="pf-error">${icon('error', 16)}<span>${escapeHtml(msg)}</span></div>`;
}
function muted(txt) { return `<p class="pf-muted">${escapeHtml(txt)}</p>`; }

function badgeStatus(status) {
  return status === 'SUSPENDED'
    ? `<span class="pf-badge pf-badge-suspended">${escapeHtml(t('platform.status.suspended'))}</span>`
    : `<span class="pf-badge pf-badge-active">${escapeHtml(t('platform.status.active'))}</span>`;
}

function listHtml() {
  if (s.loading) return muted(t('platform.loading'));
  let h = `<div class="pf-tabbar">
      <p class="pf-subtitle">${escapeHtml(t('platform.subtitle'))}</p>
      <button class="pf-btn pf-btn-ghost" data-action="trash">${icon('delete', 18)}<span>${escapeHtml(t('platform.trash.title'))}</span></button>
    </div>`;
  if (s.error) h += errBox(s.error);
  if (s.inviteUrl) h += createdBox();
  h += s.creating
    ? newFormHtml()
    : `<div class="pf-toolbar"><button class="pf-btn pf-btn-primary" data-action="new">${icon('add', 18)}<span>${escapeHtml(t('platform.newCompany'))}</span></button></div>`;
  h += `<div class="pf-list">${s.orgs.map(orgCard).join('')}</div>`;
  return h;
}

function orgCard(o) {
  const badge = o.isPlatform ? `<span class="pf-badge pf-badge-platform">${escapeHtml(t('platform.platformBadge'))}</span>` : '';
  return `
    <button class="pf-card" data-action="open" data-id="${escapeHtml(o.id)}">
      <div class="pf-card-main">
        <div class="pf-card-name">${escapeHtml(o.name)} ${badge}</div>
        <div class="pf-card-slug">${escapeHtml(o.slug)}</div>
      </div>
      <div class="pf-card-counts">
        <span title="${escapeHtml(t('platform.counts.users'))}">${icon('group', 16)} ${o._count.users}</span>
        <span title="${escapeHtml(t('platform.counts.connections'))}">${icon('cable', 16)} ${o._count.connections}</span>
        <span title="${escapeHtml(t('platform.counts.scenarios'))}">${icon('account_tree', 16)} ${o._count.scenarios}</span>
      </div>
      ${badgeStatus(o.status)}
      ${icon('chevron_right', 20)}
    </button>`;
}

function createdBox() {
  const hint = s.inviteMailSent
    ? t('platform.created.mailSent', { email: s.inviteEmail })
    : t('platform.created.mailFailed');
  return `
    <div class="pf-success">
      <div class="pf-success-head">${icon('check_circle', 18)}<span>${escapeHtml(t('platform.created.title'))}</span></div>
      <p class="pf-muted">${escapeHtml(hint)}</p>
      <div class="pf-invite">
        <input class="pf-input" readonly value="${escapeHtml(s.inviteUrl)}" onclick="this.select()" />
        <button class="pf-btn" data-action="copy" data-url="${escapeHtml(s.inviteUrl)}">${escapeHtml(t('platform.created.copy'))}</button>
      </div>
      <div class="pf-row-gap"><button class="pf-btn pf-btn-ghost" data-action="dismiss-invite">${escapeHtml(t('platform.created.done'))}</button></div>
    </div>`;
}

function newFormHtml() {
  return `
    <form id="pf-new-form" class="pf-form">
      ${s.createError ? errBox(s.createError) : ''}
      <label class="pf-field"><span>${escapeHtml(t('platform.form.name'))}</span>
        <input class="pf-input" name="name" required autocomplete="off" /></label>
      <label class="pf-field"><span>${escapeHtml(t('platform.form.slug'))}</span>
        <input class="pf-input" name="slug" pattern="[a-z0-9-]+" minlength="2" maxlength="40" required autocomplete="off" />
        <small class="pf-hint">${escapeHtml(t('platform.form.slugHint'))}</small></label>
      <label class="pf-field"><span>${escapeHtml(t('platform.form.ownerEmail'))}</span>
        <input class="pf-input" type="email" name="ownerEmail" required autocomplete="off" /></label>
      <label class="pf-field"><span>${escapeHtml(t('platform.form.ownerName'))}</span>
        <input class="pf-input" name="ownerName" autocomplete="off" /></label>
      <div class="pf-field"><span>${escapeHtml(t('platform.form.modules'))}</span>
        ${moduleTiles([], 'modules')}
        <small class="pf-hint">${escapeHtml(t('platform.modules.hint'))}</small></div>
      <div class="pf-row-gap">
        <button type="submit" class="pf-btn pf-btn-primary" ${s.creatingBusy ? 'disabled' : ''}>
          ${escapeHtml(s.creatingBusy ? t('platform.form.create') + '…' : t('platform.form.create'))}
        </button>
        <button type="button" class="pf-btn pf-btn-ghost" data-action="cancel-new">${escapeHtml(t('platform.form.cancel'))}</button>
      </div>
    </form>`;
}

// کاشی‌های ماژول — selected=[] یعنی Vollzugang (هیچ کاشی روشنی).
// toggle فقط کلاس active را عوض می‌کند (بدون draw)، جمع‌آوری موقع ذخیره از DOM.
function moduleTiles(selected, group) {
  return `<div class="pf-modules">${MODULE_CATALOG.map(({ key: m, icon: moduleIcon }) => `
    <button type="button" class="pf-module-tile ${selected.includes(m) ? 'active' : ''}"
      data-action="toggle-module" data-group="${group}" data-module="${m}">
      ${icon(moduleIcon, 20)}
      <span>${escapeHtml(t('platform.modules.' + m))}</span>
      <span class="pf-module-check">${icon('check_circle', 16)}</span>
    </button>`).join('')}</div>`;
}

function activeModules(group) {
  return [...overlay.querySelectorAll(`.pf-module-tile.active[data-group="${group}"]`)]
    .map((el) => el.dataset.module);
}

/** Pilot der Ausschreibungs-Prüfung (E5): was die Firma mit den Befunden gemacht hat, je Regel. */
function feedbackHtml(feedback) {
  if (!feedback.findings) return muted(t('platform.tenderAuditFeedback.empty'));
  const label = (rule) => rule.ruleKey === 'conditions_llm'
    ? t('platform.tenderAuditFeedback.aiRule', { category: t(`ausschreibung.audit.category.${rule.category || 'other'}`) })
    // Lücken je Raumtyp (F14): per topic — one the company keeps confirming is a candidate for a fixed rule.
    : rule.ruleKey === 'room_gaps_llm'
      ? t('platform.tenderAuditFeedback.roomRule', { topic: t(`ausschreibung.audit.roomTopic.${rule.category || 'other'}`) })
      : t(`ausschreibung.audit.rule.${rule.ruleKey}`);
  const rows = feedback.rules.map((rule) => `
    <div class="pf-row pf-feedback">
      <div class="pf-feedback-main">
        <strong>${escapeHtml(label(rule))}</strong>
        <small>${escapeHtml(t('platform.tenderAuditFeedback.counts', { open: rule.open, useful: rule.useful, dismissed: rule.dismissed }))}</small>
        ${rule.reasons.length ? `<small class="pf-feedback-reasons">${escapeHtml(t('platform.tenderAuditFeedback.reasons', { reasons: rule.reasons.join(' · ') }))}</small>` : ''}
      </div>
      <span class="pf-feedback-rate">${rule.falseAlarmRate === null ? '—' : escapeHtml(t('platform.tenderAuditFeedback.rate', { percent: fmtPercent(rule.falseAlarmRate * 100, { decimals: 0 }) }))}</span>
    </div>`).join('');
  return `<small class="pf-hint">${escapeHtml(t('platform.tenderAuditFeedback.summary', { findings: feedback.findings, tenders: feedback.tenders }))}</small>
    <div class="pf-list">${rows}</div>
    <small class="pf-hint">${escapeHtml(t('platform.tenderAuditFeedback.hint'))}</small>
    ${feedback.rules.some((rule) => rule.ruleKey === 'room_gaps_llm') ? `<small class="pf-hint">${escapeHtml(t('platform.tenderAuditFeedback.roomHint'))}</small>` : ''}`;
}

function section(title, inner) {
  return `<div class="pf-section"><h3 class="pf-section-title">${escapeHtml(title)}</h3><div class="pf-section-body">${inner}</div></div>`;
}

function detailHtml() {
  if (s.loading) return muted(t('platform.loading'));
  if (s.error || !s.detail) return `${errBox(s.error || t('platform.errors.generic'))}<button class="pf-btn pf-btn-ghost" data-action="back">${escapeHtml(t('platform.actions.back'))}</button>`;
  const o = s.detail;
  const suspended = o.status === 'SUSPENDED';
  const statusBtn = o.isPlatform ? '' : `
    <button class="pf-btn ${suspended ? 'pf-btn-primary' : ''}" data-action="toggle-status" data-id="${escapeHtml(o.id)}" data-status="${escapeHtml(o.status)}">
      ${escapeHtml(suspended ? t('platform.actions.activate') : t('platform.actions.suspend'))}
    </button>`;

  return `
    <div class="pf-detail-head">
      <button class="pf-icon-btn" data-action="back">${icon('arrow_back', 20)}</button>
      <div class="pf-detail-titles">
        <div class="pf-detail-name">${escapeHtml(o.name)}
          ${o.isPlatform ? `<span class="pf-badge pf-badge-platform">${escapeHtml(t('platform.platformBadge'))}</span>` : ''}
          ${badgeStatus(o.status)}
        </div>
        <div class="pf-card-slug">${escapeHtml(o.slug)}</div>
      </div>
      ${statusBtn}
    </div>

    ${section(t('platform.detail.members'), `
      ${o.users && o.users.length
        ? o.users.map((u) => `<div class="pf-row">
            <span class="pf-row-main">${escapeHtml(u.name || u.email)}</span>
            <span class="pf-row-sub">${escapeHtml(u.email)}</span>
            <span class="pf-tag">${escapeHtml(u.role ? u.role.name : t('platform.detail.noRole'))}</span>
            <span class="pf-badge ${u.status === 'ACTIVE' ? 'pf-badge-active' : 'pf-badge-suspended'}">${escapeHtml(u.status)}</span>
          </div>`).join('')
        : muted(t('platform.detail.noMembers'))}
      ${(o.invitations || []).map((inv) => `<div class="pf-row">
          <span class="pf-row-main">${escapeHtml(inv.email)}</span>
          <span class="pf-row-sub">${escapeHtml(t('platform.invite.until'))} ${escapeHtml(fmtDate(inv.expiresAt))}</span>
          <span class="pf-tag">${escapeHtml(inv.role ? inv.role.name : t('platform.detail.noRole'))}</span>
          <span class="pf-badge pf-badge-pending">${escapeHtml(t('platform.invite.pendingTag'))}</span>
        </div>`).join('')}
      ${o.isPlatform ? '' : inviteMemberHtml(o)}`)}

    ${section(t('platform.detail.connections'), o.connections && o.connections.length
      ? o.connections.map((c) => `<div class="pf-row">
          <span class="pf-row-main">${escapeHtml(c.provider)}</span>
          <span class="pf-row-sub">${escapeHtml(c.email || '')}</span>
          <span class="pf-badge ${c.isValid ? 'pf-badge-active' : 'pf-badge-suspended'}">${c.isValid ? 'OK' : '!'}</span>
        </div>`).join('')
      : muted(t('platform.detail.noConnections')))}

    ${section(t('platform.detail.scenarios'), o.scenarios && o.scenarios.length
      ? o.scenarios.map((sc) => `<div class="pf-row">
          <span class="pf-row-main">${escapeHtml(sc.name)}</span>
          <span class="pf-badge ${sc.isActive ? 'pf-badge-active' : ''}">${sc.isActive ? 'active' : 'off'}</span>
        </div>`).join('')
      : muted(t('platform.detail.noScenarios')))}

    ${o.isPlatform ? '' : section(t('platform.detail.modules'), `
      ${moduleTiles(o.enabledModules || [], 'detailModules')}
      <small class="pf-hint">${escapeHtml(t('platform.modules.hint'))}</small>
      <div class="pf-row-gap">
        <button class="pf-btn pf-btn-primary" data-action="save-modules" data-id="${escapeHtml(o.id)}">${escapeHtml(t('platform.modules.save'))}</button>
      </div>`)}

    ${section(t('platform.maxSessions.title'), `
      <div class="pf-maxsessions">
        <label class="pf-maxsessions-label" for="pf-maxsessions">${escapeHtml(t('platform.maxSessions.label'))}</label>
        <input class="pf-input pf-maxsessions-input" id="pf-maxsessions" type="number"
               min="1" max="20" step="1" value="${Number(o.maxSessions) || 1}">
        <button class="pf-btn pf-btn-primary" data-action="save-maxsessions" data-id="${escapeHtml(o.id)}">${escapeHtml(t('platform.maxSessions.save'))}</button>
      </div>
      <small class="pf-hint">${escapeHtml(
        Number(o.maxSessions) === 1 ? t('platform.maxSessions.hintOne') : t('platform.maxSessions.hint'),
      )}</small>`)}

    ${o.isPlatform ? '' : section(t('platform.exempt.title'), `
      <button type="button" class="pf-module-tile pf-exempt ${o.subscriptionExempt ? 'active' : ''}"
        data-action="toggle-exempt" data-id="${escapeHtml(o.id)}" data-enabled="${o.subscriptionExempt ? '1' : '0'}">
        ${icon('all_inclusive', 20)}
        <span>${escapeHtml(t('platform.exempt.tile'))}</span>
        <span class="pf-module-check">${icon('check_circle', 16)}</span>
      </button>
      <small class="pf-hint">${escapeHtml(t('platform.exempt.hint'))}</small>`)}

    ${o.isPlatform ? '' : section(t('platform.tenderAuditAi.title'), `
      <button type="button" class="pf-module-tile pf-exempt ${o.tenderAuditSettings?.enabledAt ? 'active' : ''}"
        data-action="toggle-tender-audit-ai" data-id="${escapeHtml(o.id)}" data-enabled="${o.tenderAuditSettings?.enabledAt ? '1' : '0'}">
        ${icon('fact_check', 20)}
        <span>${escapeHtml(t('platform.tenderAuditAi.tile'))}</span>
        <span class="pf-module-check">${icon('check_circle', 16)}</span>
      </button>
      <small class="pf-hint">${escapeHtml(t(o.tenderAuditSettings?.aiConsentAt ? 'platform.tenderAuditAi.consented' : 'platform.tenderAuditAi.hint'))}</small>`)}

    ${o.tenderAuditFeedback ? section(t('platform.tenderAuditFeedback.title'), feedbackHtml(o.tenderAuditFeedback)) : ''}

    ${section(t('platform.detail.audit'), s.audit && s.audit.length
      ? s.audit.map(auditRow).join('')
      : muted(t('platform.detail.noAudit')))}

    ${o.isPlatform ? '' : dangerZone(o)}
  `;
}

// فرم دعوت عضو در جزئیات شرکت + نمایش لینکِ ساخته‌شده
function inviteMemberHtml(o) {
  const linkBox = s.inviteMemberUrl ? `
    <div class="pf-success">
      <div class="pf-success-head">${icon('check_circle', 18)}<span>${escapeHtml(t('platform.invite.created'))}</span></div>
      <p class="pf-muted">${escapeHtml(s.inviteMemberMailSent
        ? t('platform.invite.mailSent', { email: s.inviteMemberEmail })
        : t('platform.invite.mailFailed'))}</p>
      <div class="pf-invite">
        <input class="pf-input" readonly value="${escapeHtml(s.inviteMemberUrl)}" onclick="this.select()" />
        <button class="pf-btn" data-action="copy" data-url="${escapeHtml(s.inviteMemberUrl)}">${escapeHtml(t('platform.created.copy'))}</button>
      </div>
      <div class="pf-row-gap"><button class="pf-btn pf-btn-ghost" data-action="dismiss-member-invite">${escapeHtml(t('platform.created.done'))}</button></div>
    </div>` : '';

  const roles = (o.roles || []);
  return `
    ${linkBox}
    ${s.inviteMemberError ? errBox(s.inviteMemberError) : ''}
    <form id="pf-invite-form" class="pf-invite-form" data-id="${escapeHtml(o.id)}">
      <input class="pf-input" type="email" name="inviteEmail" required autocomplete="off"
        placeholder="${escapeHtml(t('platform.invite.emailPlaceholder'))}" />
      <select class="pf-input pf-invite-role" name="inviteRole">
        ${roles.map((r) => `<option value="${escapeHtml(r.id)}">${escapeHtml(r.name)}</option>`).join('')}
        <option value="">${escapeHtml(t('platform.invite.noRoleOption'))}</option>
      </select>
      <button type="submit" class="pf-btn pf-btn-primary" ${s.inviteMemberBusy ? 'disabled' : ''}>
        ${icon('person_add', 18)}<span>${escapeHtml(t('platform.invite.action'))}</span>
      </button>
    </form>`;
}

function dangerZone(o) {
  if (s.confirmDelete) {
    return `<div class="pf-danger">
      <div class="pf-danger-text">
        <strong>${escapeHtml(t('platform.deleteConfirm.question'))}</strong>
        <span class="pf-muted">${escapeHtml(t('platform.deleteConfirm.hint'))}</span>
      </div>
      <div class="pf-row-gap">
        <button class="pf-btn pf-btn-danger" data-action="confirm-delete" data-id="${escapeHtml(o.id)}">${escapeHtml(t('platform.deleteConfirm.confirm'))}</button>
        <button class="pf-btn pf-btn-ghost" data-action="cancel-delete">${escapeHtml(t('platform.deleteConfirm.cancel'))}</button>
      </div>
    </div>`;
  }
  return `<div class="pf-danger">
    <span class="pf-muted">${escapeHtml(t('platform.deleteConfirm.hint'))}</span>
    <button class="pf-btn pf-btn-danger" data-action="ask-delete">${icon('delete', 18)}<span>${escapeHtml(t('platform.actions.delete'))}</span></button>
  </div>`;
}

function auditRow(l) {
  const who = l.actor ? l.actor.email : '—';
  const when = fmtDateTime(l.createdAt);
  const pf = l.isPlatformAction ? `<span class="pf-tag pf-tag-platform">${escapeHtml(t('platform.detail.platformAction'))}</span>` : '';
  return `<div class="pf-audit">
    <span class="pf-audit-action">${escapeHtml(l.action)}</span>
    <span class="pf-row-sub">${escapeHtml(t('platform.detail.by'))} ${escapeHtml(who)}</span>
    ${pf}
    <span class="pf-audit-time">${escapeHtml(when)}</span>
  </div>`;
}

function trashHtml() {
  let h = `<div class="pf-detail-head">
      <button class="pf-icon-btn" data-action="back">${icon('arrow_back', 20)}</button>
      <div class="pf-detail-titles">
        <div class="pf-detail-name">${icon('delete', 20)} ${escapeHtml(t('platform.trash.title'))}</div>
      </div>
    </div>`;
  if (s.loading) return h + muted(t('platform.loading'));
  if (s.error) h += errBox(s.error);
  if (!s.trash.length) return h + muted(t('platform.trash.empty'));

  h += `<div class="pf-list">${s.trash.map(trashCard).join('')}</div>`;
  return h;
}

function trashCard(o) {
  const when = fmtDateTime(o.deletedAt);
  const purging = s.purgeConfirmId === o.id;
  const purgeCtl = purging
    ? `<button class="pf-btn pf-btn-danger" data-action="confirm-purge" data-id="${escapeHtml(o.id)}">${escapeHtml(t('platform.trash.confirmForever'))}</button>
       <button class="pf-btn pf-btn-ghost" data-action="cancel-purge">${escapeHtml(t('platform.deleteConfirm.cancel'))}</button>`
    : `<button class="pf-btn pf-btn-danger" data-action="ask-purge" data-id="${escapeHtml(o.id)}">${escapeHtml(t('platform.trash.deleteForever'))}</button>`;

  return `
    <div class="pf-card pf-card-static">
      <div class="pf-card-main">
        <div class="pf-card-name">${escapeHtml(o.name)}</div>
        <div class="pf-card-slug">${escapeHtml(o.slug)} · ${escapeHtml(t('platform.trash.deleted'))} ${escapeHtml(when)}</div>
        ${purging ? `<div class="pf-purge-hint">${escapeHtml(t('platform.trash.purgeHint'))}</div>` : ''}
      </div>
      <div class="pf-row-gap">
        <button class="pf-btn" data-action="restore" data-id="${escapeHtml(o.id)}">${icon('restore_from_trash', 18)}<span>${escapeHtml(t('platform.trash.restore'))}</span></button>
        ${purgeCtl}
      </div>
    </div>`;
}
